import { ArrowRight, BookOpen, User } from "lucide-react";

/* Dashboard-style preview that mirrors the actual Verbatim app */
const lang = [
  {
    code: "PL",
    flag: "🇵🇱",
    name: "Polski",
    quote: "Czy tata czyta cytaty Tacyta?",
    known: "4,138",
    stories: "38/199",
    unique: "105,820",
    total: "918,854",
    progress: 14.08,
  },
  {
    code: "DE",
    flag: "🇩🇪",
    name: "Deutsch",
    quote: "Barbaras Rhabarberbar.",
    known: "1,612",
    stories: "4/12",
    unique: "24,560",
    total: "134,210",
    progress: 3.56,
  },
];

export const Hero = ({ onCta }) => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center pt-28 pb-20"
    >
      <div className="glow-blue -top-20 -left-20" aria-hidden />
      <div className="glow-blue top-40 right-0" aria-hidden />

      <div className="container-x relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <div className="pill pill-blue mb-6" data-testid="hero-eyebrow">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--vb-primary)] animate-pulse" />
              Built for serious readers · Desktop only
            </div>

            <h1 className="font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.98]">
              Read{" "}
              <span className="text-[var(--vb-primary)] italic font-light">
                anything.
              </span>
              <br />
              Learn{" "}
              <span className="text-[var(--vb-known)] italic font-light">
                everything.
              </span>
            </h1>

            <p className="mt-7 max-w-xl text-base sm:text-lg text-[var(--vb-muted)] leading-relaxed">
              Verbatim turns the texts you already want to read — novels,
              articles, subtitles, song lyrics, your friend's blog — into a
              living dictionary. Every word you mark sticks, across every
              text. Watch your vocabulary grow and your reading speed
              skyrocket as you read through stories in your target language(s).
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                data-testid="hero-cta-primary"
                onClick={onCta}
                className="btn-primary"
              >
                Join the waitlist <ArrowRight size={16} />
              </button>
              <a
                href="#demo"
                data-testid="hero-cta-secondary"
                className="btn-ghost"
              >
                <BookOpen size={14} /> Try the reader
              </a>
            </div>
          </div>

          {/* Verbatim-style dashboard preview */}
          <div className="lg:col-span-6">
            <div className="relative" data-testid="hero-dashboard-preview">
              <div className="absolute -inset-4 bg-[var(--vb-primary)]/8 blur-3xl rounded-3xl" aria-hidden />

              <div className="relative card-soft p-5 sm:p-6">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#ef4444]/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#fbbf24]/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#34d399]/70" />
                    <span className="ml-3 mono-label">verbatim.io</span>
                  </div>
                  <span className="pill pill-known">
                    <User size={12} /> Welcome!
                  </span>
                </div>

                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="font-display font-extrabold text-3xl text-[var(--vb-primary)]">
                      Verbatim
                    </div>
                    <div className="text-[var(--vb-muted)] text-sm italic mt-0.5">
                      Read stories in your target language(s)
                    </div>
                  </div>
                </div>

                {lang.map((l, idx) => (
                  <div
                    key={idx}
                    className={`card-soft p-5 ${idx > 0 ? "mt-4" : ""}`}
                    data-testid={`hero-lang-card-${idx}`}
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-[var(--vb-surface-2)] flex items-center justify-center shrink-0 overflow-hidden border border-[var(--vb-border)]">
                        <img
                          src={`https://flagcdn.com/${l.code.toLowerCase()}.svg`}
                          alt={`${l.name} flag`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="font-display font-bold text-lg leading-tight">
                          {l.name}
                        </div>
                        <div className="text-xs text-[var(--vb-muted)] italic truncate">
                          {l.quote}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { v: l.known, lbl: "KNOWN WORDS" },
                        { v: l.stories, lbl: "READ STORIES" },
                        { v: l.unique, lbl: "UNIQUE WORDS" },
                        { v: l.total, lbl: "TOTAL WORDS" },
                      ].map((s, i) => (
                        <div
                          key={i}
                          className="bg-[var(--vb-surface-2)]/60 rounded-lg p-3 border border-[var(--vb-border)]"
                        >
                          <div className="stat-number text-base sm:text-lg leading-none">
                            {s.v}
                          </div>
                          <div className="mono-label !text-[9px] mt-1.5">{s.lbl}</div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4 flex items-center gap-3">
                      <div className="progress-bar flex-1">
                        <span style={{ width: `${Math.max(l.progress, 1.5)}%` }} />
                      </div>
                      <span className="text-xs text-[var(--vb-muted)] font-mono shrink-0">
                        {l.progress.toFixed(2)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
